import React, { useEffect, useState } from "react"
import { useNavigate, Link, useSearchParams } from "react-router-dom"
import PasswordVisibilityToggle from "../components/PasswordVisibilityToggle"
import PhoneNumberInput from "../components/PhoneNumberInput"
import AccountTypeToggle from "../components/AccountTypeToggle"
import { getAPIBaseURL } from "../services/helpers"
import { postData } from "../services/request"
import {
  validateSignupEmail,
  validatePassword,
  validateCompanyName,
  validateInternationalPhone,
  validateAddress,
  validateName,
} from "../services/validators"
import {
  parseEmploymeRequestResult,
  applyFieldErrors,
  resolveSignupErrorMessage,
} from "../services/employmeAuthErrors"
import AuthLayout from "../components/AuthLayout"
import SignupSuccessModal from "../components/SignupSuccessModal"
import { COUNTRIES } from "../data/countries"
import { getReferralAttribution } from "../services/referralAttribution"

const EMPLOYER_ERRORS = [
  { field: "organization", msg: "" },
  { field: "email", msg: "" },
  { field: "phoneNumber", msg: "" },
  { field: "password", msg: "" },
  { field: "confirmPassword", msg: "" },
  { field: "country", msg: "" },
  { field: "address", msg: "" },
]

const JOB_SEEKER_ERRORS = [
  { field: "firstName", msg: "" },
  { field: "lastName", msg: "" },
  { field: "email", msg: "" },
  { field: "phoneNumber", msg: "" },
  { field: "password", msg: "" },
  { field: "confirmPassword", msg: "" },
]

const parseAccountType = (value) => (value === "job_seeker" ? "JOB_SEEKER" : "EMPLOYER")

const EmployerSignup = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [accountType, setAccountType] = useState(() => parseAccountType(searchParams.get("role")))
  const [organization, setOrganization] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phoneCountryCode, setPhoneCountryCode] = useState("+234")
  const [phoneLocalNumber, setPhoneLocalNumber] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [country, setCountry] = useState("")
  const [address, setAddress] = useState("")
  const [btnState, setBtnState] = useState("Sign up")
  const [error, setError] = useState(EMPLOYER_ERRORS)
  const [genError, setGenError] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")

  useEffect(() => {
    const role = searchParams.get("role")
    if (role) {
      setAccountType(parseAccountType(role))
    }
  }, [searchParams])

  useEffect(() => {
    setError(accountType === "EMPLOYER" ? EMPLOYER_ERRORS : JOB_SEEKER_ERRORS)
    setGenError("")
  }, [accountType])

  const validatePasswordFields = (errs) => {
    let isValid = true
    const passwordValidation = validatePassword(password)
    const passwordErrorIndex = errs.findIndex((item) => item.field === "password")
    if (passwordErrorIndex !== -1) {
      errs[passwordErrorIndex].msg = passwordValidation.result
      if (passwordValidation.error !== "") isValid = false
    }

    const confirmPasswordErrorIndex = errs.findIndex((item) => item.field === "confirmPassword")
    if (confirmPasswordErrorIndex !== -1) {
      if (!confirmPassword.trim()) {
        errs[confirmPasswordErrorIndex].msg = "Please confirm your password"
        isValid = false
      } else if (password !== confirmPassword) {
        errs[confirmPasswordErrorIndex].msg = "Passwords do not match"
        isValid = false
      } else {
        errs[confirmPasswordErrorIndex].msg = ""
      }
    }

    return isValid
  }

  const validateEmployerForm = () => {
    let isValid = true
    const newErrors = [...error]

    const orgValidation = validateCompanyName(organization)
    const orgErrorIndex = newErrors.findIndex((item) => item.field === "organization")
    if (orgErrorIndex !== -1) {
      newErrors[orgErrorIndex].msg = orgValidation.result
      if (orgValidation.error !== "") isValid = false
    }

    const emailValidation = validateSignupEmail(email)
    const emailErrorIndex = newErrors.findIndex((item) => item.field === "email")
    if (emailErrorIndex !== -1) {
      newErrors[emailErrorIndex].msg = emailValidation.result
      if (emailValidation.error !== "") isValid = false
    }

    const phoneValidation = validateInternationalPhone(phoneCountryCode, phoneLocalNumber)
    const phoneErrorIndex = newErrors.findIndex((item) => item.field === "phoneNumber")
    if (phoneErrorIndex !== -1) {
      newErrors[phoneErrorIndex].msg = phoneValidation.result
      if (phoneValidation.error !== "") isValid = false
    }

    if (!validatePasswordFields(newErrors)) isValid = false

    const countryErrorIndex = newErrors.findIndex((item) => item.field === "country")
    if (countryErrorIndex !== -1) {
      if (!country.trim()) {
        newErrors[countryErrorIndex].msg = "Country is required"
        isValid = false
      } else {
        newErrors[countryErrorIndex].msg = ""
      }
    }

    const addressValidation = validateAddress(address)
    const addressErrorIndex = newErrors.findIndex((item) => item.field === "address")
    if (addressErrorIndex !== -1) {
      newErrors[addressErrorIndex].msg = addressValidation.result
      if (addressValidation.error !== "") isValid = false
    }

    setError(newErrors)
    return isValid
  }

  const validateJobSeekerForm = () => {
    let isValid = true
    const newErrors = [...error]

    const firstNameValidation = validateName(firstName)
    const firstNameIndex = newErrors.findIndex((item) => item.field === "firstName")
    if (firstNameIndex !== -1) {
      newErrors[firstNameIndex].msg = firstNameValidation.result
      if (firstNameValidation.error !== "") isValid = false
    }

    const lastNameValidation = validateName(lastName)
    const lastNameIndex = newErrors.findIndex((item) => item.field === "lastName")
    if (lastNameIndex !== -1) {
      newErrors[lastNameIndex].msg = lastNameValidation.result
      if (lastNameValidation.error !== "") isValid = false
    }

    const emailValidation = validateSignupEmail(email)
    const emailErrorIndex = newErrors.findIndex((item) => item.field === "email")
    if (emailErrorIndex !== -1) {
      newErrors[emailErrorIndex].msg = emailValidation.result
      if (emailValidation.error !== "") isValid = false
    }

    const phoneValidation = validateInternationalPhone(phoneCountryCode, phoneLocalNumber)
    const phoneErrorIndex = newErrors.findIndex((item) => item.field === "phoneNumber")
    if (phoneErrorIndex !== -1) {
      newErrors[phoneErrorIndex].msg = phoneValidation.result
      if (phoneValidation.error !== "") isValid = false
    }

    if (!validatePasswordFields(newErrors)) isValid = false

    setError(newErrors)
    return isValid
  }

  const signup = async () => {
    setGenError("")
    const isValid = accountType === "EMPLOYER" ? validateEmployerForm() : validateJobSeekerForm()
    if (!isValid) return

    setBtnState("Loading...")
    const url = `${getAPIBaseURL()}/employme/auth/signup`
    const headers = {}
    const phoneE164 = validateInternationalPhone(phoneCountryCode, phoneLocalNumber).e164

    const payload =
      accountType === "EMPLOYER"
        ? {
            role: "EMPLOYER",
            firstName: organization || "Employer",
            lastName: "Admin",
            email,
            phoneNumber: phoneE164,
            password,
            confirmPassword,
            companyName: organization,
            companyIndustry: "General",
            companySize: "1-10",
            companyLocation: { country, address },
            contactPersonRole: "HR",
          }
        : {
            role: "JOB_SEEKER",
            firstName,
            lastName,
            email,
            phoneNumber: phoneE164,
            password,
            confirmPassword,
          }

    const referral = getReferralAttribution()
    if (referral) {
      payload.referralCode = referral.referralCode
      payload.campaignId = referral.campaignId
      if (referral.deviceToken) payload.deviceToken = referral.deviceToken
      if (referral.visitorId) payload.visitorId = referral.visitorId
    }

    const requestResult = await postData(url, headers, payload)
    setBtnState("Sign up")

    const { ok, status, message, fieldErrors } = parseEmploymeRequestResult(requestResult)
    if (fieldErrors.length) {
      setError((prev) => applyFieldErrors(prev, fieldErrors))
    }

    if (ok && status === 201) {
      setGenError("")
      const successMsg =
        requestResult.result?.data?.data?.message ||
        message ||
        "Account created. Check your email for a verification link."
      setSuccessMessage(successMsg)
      setShowSuccessModal(true)
      return
    }

    setGenError(
      resolveSignupErrorMessage({
        status,
        message,
        fieldErrors,
        role: accountType === "EMPLOYER" ? "employer" : "job_seeker",
      })
    )
  }

  const getFieldMsg = (field) => error.find((item) => item.field === field)?.msg || ""

  return (
    <AuthLayout
      title="Create an account"
      subtitle={
        accountType === "EMPLOYER"
          ? "Join Employ Me to post jobs, review applicants, and run video interviews."
          : "Create a free account to browse jobs, apply, and track your applications."
      }
    >
      <div className="space-y-5">
        <AccountTypeToggle value={accountType} onChange={setAccountType} />

        {genError !== "" ? (
          <div className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-500">{genError}</div>
        ) : null}

        {accountType === "JOB_SEEKER" ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="firstName" className="mb-2 block text-sm font-medium text-gray-900">First name</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                type="text"
                id="firstName"
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
                placeholder="Jane"
              />
              {getFieldMsg("firstName") ? <p className="mt-1 text-sm font-semibold text-red-500">{getFieldMsg("firstName")}</p> : null}
            </div>
            <div>
              <label htmlFor="lastName" className="mb-2 block text-sm font-medium text-gray-900">Last name</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                type="text"
                id="lastName"
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
                placeholder="Doe"
              />
              {getFieldMsg("lastName") ? <p className="mt-1 text-sm font-semibold text-red-500">{getFieldMsg("lastName")}</p> : null}
            </div>
          </div>
        ) : (
          <div>
            <label htmlFor="organization" className="mb-2 block text-sm font-medium text-gray-900">
              Company / Organisation Name
            </label>
            <input
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              type="text"
              name="organization"
              id="organization"
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
              placeholder="Acme Inc."
            />
            {getFieldMsg("organization") ? (
              <p className="mt-1 text-sm font-semibold text-red-500">{getFieldMsg("organization")}</p>
            ) : null}
          </div>
        )}

        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-900">
            {accountType === "EMPLOYER" ? "Work email" : "Email"}
          </label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            name="email"
            id="email"
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
            placeholder={accountType === "EMPLOYER" ? "you@company.com" : "name@example.com"}
          />
          {getFieldMsg("email") ? <p className="mt-1 text-sm font-semibold text-red-500">{getFieldMsg("email")}</p> : null}
        </div>

        <div>
          <label htmlFor="phoneNumber" className="mb-2 block text-sm font-medium text-gray-900">Phone Number</label>
          <PhoneNumberInput
            id="phoneNumber"
            countryCode={phoneCountryCode}
            localNumber={phoneLocalNumber}
            onCountryCodeChange={setPhoneCountryCode}
            onLocalNumberChange={setPhoneLocalNumber}
          />
          <p className="mt-1 text-xs text-gray-500">e.g. +234 801 234 5678</p>
          {getFieldMsg("phoneNumber") ? <p className="mt-1 text-sm font-semibold text-red-500">{getFieldMsg("phoneNumber")}</p> : null}
        </div>

        <div>
          <label htmlFor="password" className="mb-2 block text-sm font-medium text-gray-900">Password</label>
          <div className="relative">
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type={showPassword ? "text" : "password"}
              name="password"
              id="password"
              placeholder="••••••••"
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-10 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
            />
            <PasswordVisibilityToggle visible={showPassword} onToggle={() => setShowPassword((prev) => !prev)} />
          </div>
          {getFieldMsg("password") ? <p className="mt-1 text-sm font-semibold text-red-500">{getFieldMsg("password")}</p> : null}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="mb-2 block text-sm font-medium text-gray-900">Confirm Password</label>
          <div className="relative">
            <input
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              id="confirmPassword"
              placeholder="••••••••"
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-10 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
            />
            <PasswordVisibilityToggle
              visible={showConfirmPassword}
              onToggle={() => setShowConfirmPassword((prev) => !prev)}
            />
          </div>
          {getFieldMsg("confirmPassword") ? (
            <p className="mt-1 text-sm font-semibold text-red-500">{getFieldMsg("confirmPassword")}</p>
          ) : null}
        </div>

        {accountType === "EMPLOYER" ? (
          <>
            <div>
              <label htmlFor="country" className="mb-2 block text-sm font-medium text-gray-900">Country</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                name="country"
                id="country"
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {getFieldMsg("country") ? <p className="mt-1 text-sm font-semibold text-red-500">{getFieldMsg("country")}</p> : null}
            </div>

            <div>
              <label htmlFor="address" className="mb-2 block text-sm font-medium text-gray-900">Address</label>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                name="address"
                id="address"
                rows="3"
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
                placeholder="Enter company full address"
              />
              {getFieldMsg("address") ? <p className="mt-1 text-sm font-semibold text-red-500">{getFieldMsg("address")}</p> : null}
            </div>
          </>
        ) : null}

        <button
          onClick={() => signup()}
          type="submit"
          className="w-full rounded-lg bg-[#00A5E6] px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-[#008FCC] focus:outline-none focus:ring-4 focus:ring-[#cceffd]"
        >
          {btnState}
        </button>

        <p className="text-sm font-light text-gray-500">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-blue-600 hover:underline">Sign in</Link>
        </p>
      </div>

      {showSuccessModal ? (
        <SignupSuccessModal message={successMessage} onContinue={() => navigate("/login")} />
      ) : null}
    </AuthLayout>
  )
}

export default EmployerSignup
